import { useCallback, useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import "./LineGraph.scss";
import { appendOnce } from "../../utils/appendOnce";
import { Tooltip } from "./Tooltip";
import { AnimatePresence, motion } from "framer-motion";

export function LineGraph({
	graph,
	type = "count",
	comparisonGraph,
	title,
	tooltipTemplate,
	comparisonData,
	prefix = "",
}) {
	const [tooltipData, setTooltipData] = useState(null);
	const ref = useRef();
	const graphRef = useRef();
	const width = 589;
	const height = 370;
	const marginLeft = 82;
	const marginRight = 20;
	const marginBottom = 33;
	const marginTop = 15;
	const formatValue = d3.format(",");
	const formatTick = d3.format("~s");
	const graphObjects = useRef({});
	const showComparisonGraph = comparisonGraph && comparisonGraph.data.length;
	const getPosition = useCallback(() => {
		let rect = ref.current.getBoundingClientRect();
		let scrollParent = document.querySelector(".technology-profile");
		return {
			left: scrollParent.scrollLeft + rect.x,
			top: scrollParent.scrollTop + rect.y,
		};
	}, []);

	useEffect(() => {
		let svg = d3.select(graphRef.current);

		let xScale = graphObjects.current.xScale || d3.scaleLinear();
		let xDomain = d3.extent(graph.data, (o) => o.year);
		let xDomainMinMax = [
			xDomain[0] > 2015 ? 2015 : xDomain[0],
			xDomain[1] < 2020 ? 2020 : xDomain[1],
		];
		xScale = xScale
			.domain([
				xDomain[0] > 2015 ? 2015 : xDomain[0],
				xDomain[1] < 2020 ? 2020 : xDomain[1],
			])
			.nice()
			.range([marginLeft, width - marginRight - 20]);

		graphObjects.current.xScale = xScale;
		let yScale = d3.scaleLinear();
		let yMax = showComparisonGraph
			? Math.max(
					d3.max(graph.data, (o) => o.value),
					d3.max(comparisonGraph.data, (o) => o.value)
			  )
			: d3.max(graph.data, (o) => o.value);

		yScale = yScale
			.domain([0, yMax])
			.nice()
			.range([height - marginBottom, marginTop]);
		// graphObjects.current.yScale = yScale;

		let xAxis = (g) =>
			g
				.attr("transform", `translate(0, ${height - marginBottom})`)
				.call(
					d3
						.axisBottom(xScale)
						.ticks(xDomainMinMax[1] - xDomainMinMax[0], "d")
						.tickSize(-500)
				)
				.call((g) =>
					g.select(".domain").attr("stroke", null).attr("fill", "#FAFAFC")
				)
				.call((g) =>
					g
						.selectAll(".tick text")
						.attr("font-size", "14")
						.attr("transform", "translate(0, 10)")
						.attr("fill", "#000055")
				)
				.call((g) => g.selectAll(".tick line").attr("stroke", "#E5E5EE"));

		let yAxis = (g) => {
			return g
				.attr("transform", `translate(${marginLeft}, 0)`)
				.call(
					d3
						.axisLeft(yScale)
						.ticks(5)
						.tickFormat(
							type === "value"
								? (d) => prefix + formatTick(d).replace(/G/, "B")
								: null
						)
						.tickSize(0)
				)
				.call((g) => g.select(".domain").remove())
				.call((g) =>
					g
						.selectAll(".tick text")
						.attr("font-size", "14")
						.attr("font-family", "Moderat")
						.attr("transform", "translate(-15, 0)")
						.attr("fill", "#000055")
				);
		};
		// svg.append("g").call(yAxis);
		let yAxisDom =
			graphObjects.current.yAxisDom || svg.append("g").attr("class", "y-axis");
		graphObjects.current.yAxisDom = yAxisDom;
		yAxisDom.transition().duration(800).call(yAxis);
		appendOnce(svg, "g.x.axis").call(xAxis);

		//Base Data
		const line = d3
			.line()
			.defined((d) => !isNaN(d.value))
			.x((d) => xScale(d.year))
			.y((d) => yScale(d.value));
		let path = graphObjects.current.path || svg.append("g");
		graphObjects.current.path = path;
		let segments = [];
		for (let i = 0; i < graph.data.length - 1; i++) {
			segments.push([graph.data[i], graph.data[i + 1]]);
		}
		let pathTransitionDuration = 400;
		path
			.selectAll("path")
			.data(segments, (d) => `${d[0].year}-${d[1].year}`)
			.join(
				(enter) => {
					return enter
						.append("path")
						.attr("fill", "none")
						.attr("stroke", "#000055")
						.attr("stroke-width", 1.5)
						.attr("d", line)
						.attr("stroke-dasharray", (d, i, path) => path[i].getTotalLength())
						.attr("stroke-dashoffset", (d, i, path) => path[i].getTotalLength())
						.call((g) => {
							g.transition()
								.duration(pathTransitionDuration)
								.delay((d, i) => pathTransitionDuration * i)
								.easeVarying((d, i) => {
									if (i === 0) {
										return d3.easeQuadIn;
									} else if (i === segments.length - 1) {
										return d3.easeQuadOut;
									}
									return d3.easeLinear;
								})
								.attr("stroke-dashoffset", 0)
								.on("end", (d, i, nodes) => {
									d3.select(nodes[i])
										.attr("stroke-dasharray", null)
										.attr("stroke-dashoffset", null);
								});
						});
				},
				(update) => {
					return update.call((g) =>
						g.transition("move").duration(800).attr("d", line)
					);
				}
			);
		let pointGroup =
			graphObjects.current.pointGroup ||
			svg.append("g").attr("class", "pointGroup");
		graphObjects.current.pointGroup = pointGroup;
		pointGroup
			.selectAll("circle")
			.data(graph.data, (d) => d.year)
			.join(
				(enter) =>
					enter
						.append("circle")
						.attr("cx", (d) => xScale(d.year))
						.attr("cy", (d) => yScale(d.value))
						.attr("r", 5.5)
						.attr("fill", "#FAFAFC")
						.attr("stroke", "#000055")
						.attr("stroke-width", 3)
						.attr("opacity", 0)
						.call((g) =>
							g
								.transition()
								.duration(pathTransitionDuration)
								.delay((d, i) => pathTransitionDuration * i)
								.attr("opacity", 1)
						),
				(update) =>
					update.call((g) =>
						g
							.transition("move")
							.duration(800)
							.attr("cx", (d) => xScale(d.year))
							.attr("cy", (d) => yScale(d.value))
					)
			)

			.on("mouseover", (e, data) => {
				let position = e.target.getBoundingClientRect();
				let scrollParent = document.querySelector(".technology-profile");
				setTooltipData({
					data: { ...data, title: title },
					position: {
						left: scrollParent.scrollLeft + position.left + position.width / 2,
						top: scrollParent.scrollTop + position.top,
					},
				});
			})
			.on("mouseout", (e, data) => {
				setTooltipData(null);
			});
		let comparisonGraphData = showComparisonGraph ? comparisonGraph.data : [];
		//Comparison Data
		let comparisonSegments = [];
		if (showComparisonGraph) {
			for (let i = 0; i < comparisonGraph.data.length - 1; i++) {
				comparisonSegments.push([
					comparisonGraph.data[i],
					comparisonGraph.data[i + 1],
				]);
			}
		}
		let path2 = graphObjects.current.path2 || svg.append("g");
		graphObjects.current.path2 = path2;
		path2
			.selectAll("path")
			.data(comparisonSegments, (d, idx) => {
				return `${d[0].year}-${d[1].year}-${comparisonGraphData.length}-${idx}`;
			})
			.join(
				(enter) => {
					return enter
						.append("path")
						.attr("fill", "none")
						.attr("stroke", "#00A4DA")
						.attr("stroke-width", 1.5)
						.attr("d", line)
						.attr("stroke-dasharray", (d, i, path) => path[i].getTotalLength())
						.attr("stroke-dashoffset", (d, i, path) => path[i].getTotalLength())
						.attr("opacity", 1)
						.call((g) => {
							g.transition()
								.duration(pathTransitionDuration)
								.delay((d, i) => pathTransitionDuration * i)
								.easeVarying((d, i) => {
									if (i === 0) {
										return d3.easeQuadIn;
									} else if (i === comparisonSegments.length - 1) {
										return d3.easeQuadOut;
									}
									return d3.easeLinear;
								})
								.attr("stroke-dashoffset", 0)
								.on("end", (d, i, nodes) => {
									d3.select(nodes[i])
										.attr("stroke-dasharray", null)
										.attr("stroke-dashoffset", null);
								});
						});
				},
				(update) => {
					return update.call((g) =>
						g.transition("move").duration(800).attr("d", line)
					);
				},
				(exit) => {
					exit
						.interrupt("enter")
						.interrupt("move")
						.transition("hide")
						.duration(300)
						.attr("opacity", 0)
						.remove();
				}
			);

		let pointGroup2 =
			graphObjects.current.pointGroup2 ||
			svg.append("g").attr("class", "pointGroup");
		graphObjects.current.pointGroup2 = pointGroup2;

		pointGroup2
			.selectAll("circle")
			.data(
				comparisonGraphData,
				(d, idx) => `${d.year}-${comparisonGraphData.length}-${idx}`
			)
			.join(
				(enter) =>
					enter
						.append("circle")
						.attr("cx", (d) => xScale(d.year))
						.attr("cy", (d) => yScale(d.value))
						.attr("r", 5.5)
						.attr("fill", "#FAFAFC")
						.attr("stroke", "#00A4DA")
						.attr("stroke-width", 3)
						.attr("opacity", 0)
						.call((g) =>
							g
								.transition("enter")
								.duration(pathTransitionDuration)
								.delay((d, i) => pathTransitionDuration * i)
								.attr("opacity", 1)
						),
				(update) =>
					update.call((g) =>
						g
							.transition("move")
							.duration(800)
							.attr("cx", (d) => xScale(d.year))
							.attr("cy", (d) => yScale(d.value))
					),
				(exit) => {
					exit
						.interrupt("enter")
						.interrupt("move")
						.transition("hide")
						.duration(300)
						.attr("opacity", 0)
						.remove();
				}
			)

			.on("mouseover", (e, data) => {
				let position = e.target.getBoundingClientRect();
				let scrollParent = document.querySelector(".technology-profile");
				setTooltipData({
					data: { ...data, title: title },
					position: {
						left: scrollParent.scrollLeft + position.left + position.width / 2,
						top: scrollParent.scrollTop + position.top,
					},
				});
			})
			.on("mouseout", (e, data) => {
				setTooltipData(null);
			});
		// } else {
		// 	if (graphObjects.current.path2) {
		// 		graphObjects.current.path2.remove();
		// 		graphObjects.current.path2 = null;
		// 	}
		// 	if (graphObjects.current.pointGroup2) {
		// 		graphObjects.current.pointGroup2.remove();
		// 		graphObjects.current.pointGroup2 = null;
		// 	}
		// }
	}, [
		graphRef,
		graph,
		comparisonGraph,
		comparisonData,
		title,
		showComparisonGraph,
		formatTick,
		prefix,
		type,
	]);
	return (
		<div className={"line-graph"} ref={ref}>
			<h3 className={"line-graph__title"}>{graph.title}</h3>
			<svg
				className={"line-graph__graph"}
				viewBox={`0 0 ${width} ${height}`}
				ref={graphRef}
			/>
			<AnimatePresence>
				{tooltipData && (
					<Tooltip
						getBasePosition={getPosition}
						format={formatValue}
						prefix={prefix}
						data={tooltipData}
						contentTemplate={tooltipTemplate}
					/>
				)}
			</AnimatePresence>
			<AnimatePresence exitBeforeEnter={true}>
				<motion.div
					key={`total-container-${comparisonGraph ? "comparison" : "standard"}`}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className={"line-graph__total-container"}
					style={{ "--row": comparisonGraph ? 2 : 4 }}>
					<h5>{graph.totalLabel}</h5>
					{!comparisonGraph ? (
						<div className={"line-graph__total-value"}>
							{prefix}
							{type === "value"
								? formatValue(d3.sum(graph.data, (o) => o.value))
								: d3.sum(graph.data, (o) => o.value)}
						</div>
					) : null}
				</motion.div>
			</AnimatePresence>
			<AnimatePresence exitBeforeEnter={true}>
				{comparisonGraph ? (
					<motion.div
						key={`comparison-value-${comparisonData.slug}`}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className={"line-graph__comparison-value"}>
						<h6>{comparisonData.title}</h6>
						{showComparisonGraph ? (
							<div className={"line-graph__comparison-total"}>
								{prefix}
								{type === "value"
									? formatValue(d3.sum(comparisonGraph.data, (o) => o.value))
									: d3.sum(comparisonGraph.data, (o) => o.value)}
							</div>
						) : (
							<div className={"line-graph__comparison-no-data"}>
								Data Unavailable
							</div>
						)}
					</motion.div>
				) : null}
			</AnimatePresence>
			<AnimatePresence exitBeforeEnter={true}>
				{comparisonGraph ? (
					<motion.div
						key={"main-value"}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className={"line-graph__main-value"}>
						<h6>{title}</h6>
						<div className={"line-graph__main-total"}>
							{prefix}
							{type === "value"
								? formatValue(d3.sum(graph.data, (o) => o.value))
								: d3.sum(graph.data, (o) => o.value)}
						</div>
					</motion.div>
				) : null}
			</AnimatePresence>
		</div>
	);
}
